/**
 * RangeMatrix — 13×13 grid showing a poker hand range, per wireframe 05.
 *
 * Standard PIO-style layout:
 *   - Rows labeled A, K, Q, J, T, 9, 8, 7, 6, 5, 4, 3, 2 (top to bottom)
 *   - Columns labeled the same (left to right)
 *   - Diagonal cells = pairs (AA, KK, ... 22)
 *   - Cells ABOVE diagonal (row index < column index) = suited (AKs, AQs, …)
 *   - Cells BELOW diagonal (row index > column index) = offsuit (AKo, AQo, …)
 *
 * Visual rules:
 *   - Cells in the supplied `rangeNotations` set are tinted brand-accent.
 *   - The `actualHandNotation` cell (e.g., "QJo" when villain holds Q♠J♦)
 *     gets a stronger highlight + border, per PRD §8.3 ("Villain's hole
 *     cards are NEVER drawn as isolated face-up cards — only as a
 *     highlighted cell within the range matrix").
 *
 * Helper exports:
 *   - `handNotation(hole)` — convert two Cards to "AA" / "AKs" / "QJo" form.
 *   - `FISH_PLACEHOLDER_RANGE` — V1 shell range used until the live grade
 *     pipeline supplies per-street ranges.
 */

import type { Card } from '@/types/card';
import { cn } from '@/lib/utils';

const RANKS = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'] as const;

interface RangeMatrixProps {
  /**
   * Set of hand notations in the range (e.g., {"AA", "AKs", "QJo"}).
   */
  rangeNotations: Set<string>;
  /**
   * The actual villain hand, in matching notation. When provided, that
   * cell gets a stronger highlight and a border. Pass `null` if the hand
   * never reached showdown.
   */
  actualHandNotation: string | null;
  className?: string;
}

export function RangeMatrix({
  rangeNotations,
  actualHandNotation,
  className,
}: RangeMatrixProps) {
  return (
    <div className={cn('w-full', className)}>
      <div
        className="grid w-full gap-px text-[8px] sm:text-[10px]"
        style={{ gridTemplateColumns: 'auto repeat(13, minmax(0, 1fr))' }}
      >
        {/* Top-left spacer */}
        <div />
        {/* Column header row */}
        {RANKS.map((r) => (
          <div
            key={`col-${r}`}
            className="flex items-center justify-center font-mono font-bold text-text-muted"
          >
            {r}
          </div>
        ))}

        {/* Grid rows */}
        {RANKS.map((rowRank, rowIdx) => (
          <RowFragment
            key={`row-${rowRank}`}
            rowRank={rowRank}
            rowIdx={rowIdx}
            rangeNotations={rangeNotations}
            actualHandNotation={actualHandNotation}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-secondary">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm bg-purple-600" />
          in range
        </span>
        {actualHandNotation && (
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded-sm border border-brand-accent bg-brand-accent" />
            actual hand · {actualHandNotation}
          </span>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// One matrix row (rank label cell + 13 hand cells)
// =============================================================================

function RowFragment({
  rowRank,
  rowIdx,
  rangeNotations,
  actualHandNotation,
}: {
  rowRank: string;
  rowIdx: number;
  rangeNotations: Set<string>;
  actualHandNotation: string | null;
}) {
  return (
    <>
      <div className="flex items-center justify-center pr-1 font-mono font-bold text-text-muted">
        {rowRank}
      </div>
      {RANKS.map((colRank, colIdx) => {
        const notation = cellNotation(rowIdx, colIdx);
        const inRange = rangeNotations.has(notation);
        const isActual = actualHandNotation === notation;
        return (
          <div
            key={`cell-${rowRank}-${colRank}`}
            className={cn(
              'aspect-square min-w-0 rounded-sm text-center',
              'flex items-center justify-center',
              'border border-border/40',
              isActual
                // Actual villain hand — solid gold (brand-accent). Visual
                // peak of the matrix.
                ? 'border-brand-accent bg-brand-accent font-bold text-text-on-light'
                : inRange
                  // In-range cells — bright purple. brand-primary (#552583)
                  // has too-low luminance to clearly stand out against
                  // surface-elevated on the dark felt, so we use Tailwind's
                  // purple-600 (#9333EA, same hue family at higher
                  // luminance). Paired visually with the gold actual-hand
                  // cell.
                  ? 'bg-purple-600 text-text-on-brand'
                  : 'bg-surface-elevated text-text-muted',
            )}
            title={notation}
            aria-label={notation}
          >
            <span className="hidden sm:inline">{notation}</span>
          </div>
        );
      })}
    </>
  );
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Hand notation for matrix cell (row, col).
 *   - row == col → pair (e.g., AA)
 *   - row < col → suited (higher rank first, e.g., AKs)
 *   - row > col → offsuit (higher rank first, e.g., AKo)
 */
function cellNotation(rowIdx: number, colIdx: number): string {
  const high = RANKS[Math.min(rowIdx, colIdx)];
  const low = RANKS[Math.max(rowIdx, colIdx)];
  if (rowIdx === colIdx) return `${high}${high}`;
  if (rowIdx < colIdx) return `${high}${low}s`;
  return `${high}${low}o`;
}

/**
 * Convert a pair of hole cards to its matrix-cell notation.
 *   ("A♥","K♥") → "AKs"
 *   ("Q♠","J♦") → "QJo"
 *   ("A♥","A♠") → "AA"
 */
export function handNotation(hole: [Card, Card]): string {
  const [a, b] = hole;
  if (a.rank === b.rank) return `${a.rank}${a.rank}`;
  const idxA = RANKS.indexOf(a.rank as (typeof RANKS)[number]);
  const idxB = RANKS.indexOf(b.rank as (typeof RANKS)[number]);
  const high = idxA < idxB ? a.rank : b.rank;
  const low = idxA < idxB ? b.rank : a.rank;
  const suited = a.suit === b.suit;
  return `${high}${low}${suited ? 's' : 'o'}`;
}

/**
 * Placeholder Fish range — mirrors the inline PLACEHOLDER_FISH_RANGE in
 * gameEngine.ts. Used by HandResolution until the grade pipeline supplies
 * a per-street, archetype-aware range. ~38% of combos.
 */
export const FISH_PLACEHOLDER_RANGE: ReadonlySet<string> = new Set([
  // Pairs
  'AA', 'KK', 'QQ', 'JJ', 'TT', '99', '88', '77', '66', '55',
  // Suited
  'AKs', 'AQs', 'AJs', 'ATs', 'KQs', 'KJs', 'QJs', 'JTs', 'T9s', '98s', '87s', '76s',
  // Offsuit
  'AKo', 'AQo', 'AJo', 'KQo', 'QJo', 'JTo',
]);
